import $ from 'jquery'
import includes from 'lodash/includes'
import isNil from 'lodash/isNil'
import memoize from 'lodash/memoize'

import { HistoryUpdateURL } from 'api/history'
import * as Model from 'api/model'
import * as PoziUtils from 'api/utils'
import { proj4, supportedProjections } from 'utils/ProjUtils'

/**
 * Pozi API Adapter
 */
const PoziAPIData = memoize(function () {
  // Adapters
  const adapters = {
    getPostalAreas: function (lng, lat) {
      return getPostalAreas(lng, lat)
    },
    getDataGovAu: function (lng, lat, ckan) {
      return getDataGovAu(lng, lat, ckan)
    },
    getDataVicByFilter: function (typeNames, cqlfilter, maxFeaturesOverride) {
      return getDataVicByFilter(typeNames, cqlfilter, maxFeaturesOverride)
    },
    ajax: function (options) {
      return ajax(options)
    },
    ajaxAsPromise: function (options) {
      return ajaxAsPromise(options)
    },
    ajaxFailDefault: function (jqxhr, textStatus, error, options) {
      return ajaxFailDefault(jqxhr, textStatus, error, options)
    }
  }

  /**
   * Handle data requests
   * @param {float} lng Longitude
   * @param {float} lat Latitude
   */
  var getPostalAreas = function (lng, lat) {
    return new Promise(function (resolve, reject) {
      let url = 'https://nationalmap.gov.au/proxy/http://geoserver.nationalmap.nicta.com.au/admin_bnds_abs/ows'
      url += '?SERVICE=WFS&VERSION=1.1.0&REQUEST=GetFeature&srsName=EPSG%3A4326&outputFormat=application%2Fjson&'
      url += 'typeNames=admin_bnds%3APOA_2011_AUST&maxFeatures=1&'
      url += 'CQL_FILTER=INTERSECTS(the_geom%2CPOINT(' + lat + '%20' + lng + '))'

      // Send an JSON request ($.ajax because it allows a timeout)
      return PoziAPIData().ajax({
        url,
        error: function (err) {
          reject(err)
        },
        success: function (data) {
          resolve(data)
        }
      })
    })
  }

  const getCoordsInWGS84Projection = (lng, lat) => {
    const coords = { lng, lat }
    const fromProjection = Model.getState('projection', null)
    const toProjection = 'EPSG:4326'

    // TEST URL: http://localhost:3000/#site=swanhill&ckan=ckan_5368b105_7e39_4ce7_b3c9_0e1e34e3d5c9&transformer=garbage&fieldnames=Rubbish%20Next%20Pickup,Recycling%20Next%20Pickup,Green%20Waste%20Next%20Pickup,&fontsize=14&widget=true&query=23%20albert
    // fromProjection = 'EPSG:3111'
    // fromProjection = 'EPSG:4326'
    // console.debug('getCoordsInWGS84Projection', fromProjection, toProjection, [lng, lat])

    if (fromProjection && fromProjection !== toProjection) {
      if (Object.keys(supportedProjections).includes(fromProjection)) {
        try {
          const toProjectionOutput = proj4(fromProjection, toProjection, [lng, lat])
          console.log('PROJ TEST 2', toProjectionOutput)
          if (toProjectionOutput.length === 2) {
            coords.lng = toProjectionOutput[0]
            coords.lat = toProjectionOutput[1]
          } else {
            throw new Error(`Invalid projection values: ${toProjectionOutput}`)
          }
        } catch (error) {
          console.error('getCoordsInWGS84Projection - Error reprojecting', fromProjection, toProjection, error)
        }
      } else {
        console.error('getCoordsInWGS84Projection - Unsupported projection:', fromProjection)
      }
    }

    console.warn('NEW COORDS', coords)
    return coords
  }

  /**
   * Handle data requests
   * @param {float} lng Longitude
   * @param {float} lat Latitude
   * @param {string} ckan data.gov.au identifier
   */
  var getDataGovAu = function (lng, lat, ckan) {
    return new Promise(function (resolve, reject) {
      const coords = getCoordsInWGS84Projection(lng, lat)
      const maxFeatures = Model.get('config.settings.maxfeatures', 100) // This is set in templates.json

      let url = 'https://data.gov.au/geoserver/wfs'
      url += '?SERVICE=WFS&VERSION=1.1.0&REQUEST=GetFeature&srsName=EPSG%3A4326&'
      url += 'typeNames=' + ckan
      url += '&maxFeatures=' + maxFeatures + '&outputFormat=application%2Fjson&'
      url += 'CQL_FILTER=INTERSECTS(geom%2CPOINT(' + coords.lat + '%20' + coords.lng + '))'

      return PoziAPIData().ajax({
        url,
        error: function (err) {
          const errorMsg = 'datagovau load failed for ckanid ' + ckan
          PoziUtils.gtag({
            category: 'data',
            status: 'warning',
            identifier: ckan,
            action: 'ajax request',
            additionalInfo: errorMsg
          })
          reject(err)
        },
        success: function (data) {
          const msg = 'datagovau retrieved data for ckanid ' + ckan
          PoziUtils.gtag({
            category: 'data',
            status: 'success',
            identifier: ckan,
            action: 'ajax request',
            additionalInfo: msg
          })
          resolve(data)
        }
      })
    })
  }

  /**
   * Handle data requests for datavic
   * @param {string} typeNames datavic table identifiers
   * @param {string} cqlfilter datavic filter
   * @param {integer} maxFeaturesOverride max features to return
   */
  var getDataVicByFilter = function (typeNames, cqlfilter, maxFeaturesOverride) {
    return new Promise(function (resolve, reject) {
      const maxFeatures = Model.get('config.settings.maxfeatures', maxFeaturesOverride) // This is set in templates.json

      let url = 'https://opendata.maps.vic.gov.au/geoserver/ows'
      url += '?SERVICE=WFS&VERSION=1.1.0&REQUEST=GetFeature&srsName=EPSG:4326&'
      url += 'typeNames=' + typeNames
      url += '&maxFeatures=' + maxFeatures + '&outputFormat=application%2Fjson&'
      url += 'CQL_FILTER=' + cqlfilter

      return PoziAPIData().ajax({
        url,
        error: function (err) {
          const errorMsg = 'getDataVicByFilter load failed for typeNames ' + typeNames
          PoziUtils.gtag({
            category: 'data',
            status: 'warning',
            identifier: typeNames,
            action: 'ajax request',
            additionalInfo: errorMsg
          })
          return reject(err)
        },
        success: function (data) {
          const msg = 'getDataVicByFilter retrieved data for typeNames ' + typeNames
          PoziUtils.gtag({
            category: 'data',
            status: 'success',
            identifier: typeNames,
            action: 'ajax request',
            additionalInfo: msg
          })
          return resolve(data)
        }
      })
    })
  }

  /**
   * Wrapper for jQuery.ajax
   * Note this function expects an error handler to be part of the options object (but will handle if it begrudingly if it doesn't)
   * @param {json} options See jQuery.ajax for details.
   * @returns {jqXHR} Returns jqXHR object if setup successful (else undefined)
   */
  const ajax = async (options) => {
    // Send an ajax request via jQuery.ajax
    let errorMsg = ''

    if (!options) {
      errorMsg = 'DataAPI received ajax request without an options object'
      console.error(errorMsg)
      PoziUtils.gtag({
        category: 'program',
        status: 'warning',
        identifier: 'DataAPI',
        action: 'ajax request',
        additionalInfo: errorMsg
      })
      return
    }
    if (!options.url) {
      errorMsg = 'DataAPI received ajax request without a url'
      console.error(errorMsg)
      PoziUtils.gtag({
        category: 'program',
        status: 'warning',
        identifier: 'DataAPI',
        action: 'ajax request',
        additionalInfo: errorMsg
      })
      return
    }

    options.url = options.url.replace('[timestamp]', Date.now())

    if (!options.method) {
      options.method = 'GET'
    }

    // This is needed for some GeoServer instances
    if (!options.contentType && options.method === 'POST') {
      options.contentType = 'application/x-www-form-urlencoded'
    }

    if (!options.timeout) {
      options.timeout = 1000 * 120 // 120 seconds
    }
    if (!options.dataType) {
      options.dataType = 'json'
    }
    if (isNil(options.jsonp)) {
      options.jsonp = false
    }
    if (isNil(options.cache)) {
      options.cache = true
    }
    if (!options.error) {
      errorMsg = 'Warning: no error handler defined for ajax request. Adding default handler.'
      // console.log(errorMsg, options.url, options)
      options.error = function (jqxhr, textStatus, error) {
        PoziAPIData().ajaxFailDefault(jqxhr, textStatus, error, options)
      }
    }

    // Create a wrapper error function that reports all request errors to GA, except when
    // request method is 'HEAD' (usually for resource checks). If you want to report an
    // error with a HEAD request, add your custom GA call with your error handler.
    if (options.method !== 'HEAD') {
      const errorFunction = options.error
      const errorUrl = options.url
      options.error = (jqxhr, textStatus, error) => {
        PoziUtils.gtag({
          category: 'data',
          status: 'warning',
          identifier: errorUrl,
          action: 'ajax request fail'
        })

        // Call the originial error function
        return errorFunction(jqxhr, textStatus, error)
      }
    }

    if (includes(options.url, 'geoproxy.pozi.com')) {
      options.cache = false
      options.headers = {
        'x-pozi-allow-credentials': 'true',
        'x-pozi-single-origin': 'true',
        ...options.headers
      }
    }

    const appUrl = window.location.href

    // Get Azure App Proxy config from site settings
    // TODO: find a better way to retrieve the config
    const azureConf = window.appObject?.siteConfig?.azure || {}
    const {
      appProxyExternalUrl,
      appProxyInternalUrl,
      corsProxyUrl,
      authType: azureAuthType = 'token',
      enabled: azureEnabled = false
    } = azureConf

    // Check if the URL is an Azure App Proxy internal URL
    // This could come from a QGIS GetCapabilities/GetProjectSettings request and
    // will need to be translated to an App Proxy external URL for it to be accessible through Azure AD
    const isAzureAppProxyInternalUrl = appProxyInternalUrl && options.url.startsWith(appProxyInternalUrl)

    // console.debug('PoziApi MSAL: internal URL?', isAzureAppProxyInternalUrl, options.url)

    if (azureEnabled && isAzureAppProxyInternalUrl) {
      const translatedUrl = options.url.replace(appProxyInternalUrl, appProxyExternalUrl)

      // console.debug('PoziApi MSAL: translate internal URL to be external', options.url, translatedUrl)

      options.url = translatedUrl
    }

    // Check if the URL is an Azure App Proxy external URL
    const isAzureAppProxyExternalUrl = appProxyExternalUrl && options.url.startsWith(appProxyExternalUrl)

    // TODO: only check the query string length to be a mx of 2048 characters
    const urlTooLongForAzure = options.url.length > 2048

    // Convert GET to POST if the URL is too long for Azure
    if (azureEnabled && isAzureAppProxyExternalUrl && urlTooLongForAzure && options.method === 'GET' && !options.data) {
      console.log('PoziAPIData - Azure URL too long. Using POST', options.url)

      try {
        const urlObj = new URL(options.url)
        const baseUrl = `${urlObj.origin}${urlObj.pathname}`
        const searchParamsObj = Object.fromEntries([...urlObj.searchParams]) // create object from search params

        options.method = 'POST'
        options.url = baseUrl
        options.data = searchParamsObj

        // Have a better way of detecting that we are dealing with QGIS Server
        if (options.url.match(/qgisserver/i) && !options.contentType) {
          options.contentType = 'multipart/form-data' // Important for QGIS Server!!!
        }
      } catch (error) {
        console.error('PoziAPIData - Error creating URL object from URL', options.url, error)
      }
    }

    // const isEnterpriseUser = appUrl.includes('.enterprise.pozi.com') ||
    // appUrl.match('enterprise\\[true\\]') ||
    // appUrl.match('enterprise=true')

    const appInfo = window.getAppInfo()

    const isEnterpriseUser = !!appInfo.enterprise

    // Any requests that go through the Azure Application Proxy (configured to using tokens)
    // will need to be processed to:
    // - use our proxy to add CORS headers
    // - have the token added as a header
    if (azureEnabled && isEnterpriseUser && azureAuthType === 'token' && isAzureAppProxyExternalUrl) {
      // console.debug('PoziApi MSAL: request is token based and enabled for URL', options.url)

      // Work around for MS App Proxy not supporting CORS headers.
      // See: https://feedback.azure.com/d365community/idea/30470d7e-b825-ec11-b6e6-000d3a4f0789
      const fallbackCorsProxyUrl = 'https://geoproxy.pozi.com/'
      // const fallbackCorsProxyUrl = 'http://localhost:8080/' // for dev purposes

      // Check if there is a default CORS proxy URL and if it is a valid URL
      let defaultCorsProxyUrl = Model.get('app.defaults.corsProxyUrl', null)
      if (defaultCorsProxyUrl) {
        try {
          const _urlObj = new URL(defaultCorsProxyUrl)
        } catch (error) {
          console.log('PoziApi MSAL - Invalid default CORS proxy URL', defaultCorsProxyUrl)

          defaultCorsProxyUrl = null
          Model.set('app.defaults.corsProxyUrl', null) // prevent further use
        }
      }

      //  For debugging purposes
      if (corsProxyUrl) {
        // console.debug('PoziApi MSAL - Using site config provided CORS proxy URL', corsProxyUrl)
      } else if (defaultCorsProxyUrl) {
        // console.debug('PoziApi MSAL - Using default CORS proxy URL', defaultCorsProxyUrl)
      } else {
        // console.debug('PoziApi MSAL - Using fallback CORS proxy URL', fallbackCorsProxyUrl)
      }

      const proxyUrl = corsProxyUrl || defaultCorsProxyUrl || fallbackCorsProxyUrl
      options.url = `${proxyUrl}${options.url}`

      // console.log('PoziApi MSAL - Adding proxy to URL to support CORS', options.url)

      let accessTokenResponse = await window.getToken()
      // console.warn('MSAL TOKEN RESPONSE??', accessTokenResponse)

      if (!accessTokenResponse) {
        console.log('PoziApi MSAL - no token. Attempting sign in...')

        try {
          await window.signIn()
          // console.debug('PoziApi MSAL - sign in completed')

          accessTokenResponse = await window.getToken()
        } catch (error) {
          console.warn('PoziApi MSAL - sign in error', error.errorCode, error)
        }
      }

      // Add access token as a 'Bearer' header to the request
      if (accessTokenResponse) {
        // console.warn('MSAL TOKEN RESPONSE!!!', accessTokenResponse)

        const accessToken = accessTokenResponse.accessToken
        const bearer = `Bearer ${accessToken}`

        options.headers = {
          Authorization: bearer,
          ...options.headers
        }
      } else {
        console.log('PoziApi MSAL - After sign in (attempt) still no token.')
      }
    }

    // MS Azure cookie based authentication
    // Allow for authentication cookies to be sent along with the request
    if (isAzureAppProxyExternalUrl && isEnterpriseUser && azureAuthType !== 'token') {
      options.xhrFields = {
        withCredentials: true,
        ...options.xhrFields
      }
    }

    // If the XHR url is a resource check *AND* PoziApp runs on the Enterprise domain,
    // we'll attempt to authenticate if the request fails and return when successful
    const isResourceCheck = options.url.includes('/resourcecheck/')

    // console.debug('MSAL - data debug', azureAuthType, isResourceCheck, isEnterpriseUser, options.url)

    // If using Azure and we are using cookie-based authentication, add smart functionality
    // that automatically redirects to Pozi Server behind the app proxy so the user gets
    // authenticated and sent back to their originating URL
    if (azureEnabled && isAzureAppProxyExternalUrl && isResourceCheck && isEnterpriseUser && azureAuthType === 'cookie') {
      console.debug('PoziApi no MSAL: azure resource check, cookies based request. Not using MSAL.')

      // Capture any exisiting error and success function, which we may call later
      const onError = options.error
      const onSuccess = options.success

      // Preserve URL to be used inside closure functions below
      const url = options.url

      // Create new error function to handle authentication errors where
      // we will redirect to PoziServer on .msapproxy.net to force authentication
      // and then take us back to the current app URL
      options.error = (...args) => {
        const noRedirect = Model.getState('noredirect')
        if (noRedirect) {
          console.log('MSAPPROXY: Not redirecting because of \'/nodirect[true]\' in URL', url)

          const errorMsg = 'Warning: potential infinite loop detected in MS App Proxy XHR Request.'
          PoziUtils.gtag({
            category: 'program',
            status: 'warning',
            identifier: 'MSAPPPROXY',
            action: 'ajax request',
            additionalInfo: errorMsg
          })

          // Call original error function
          if (typeof onError === 'function') {
            console.log('MSAPPROXY: Calling original error function')
            onError.apply(onError, args)
          }
          return
        }

        // Create redirect URL
        // Note: for it to work with MS Online authentication, we need to URL encode it twice!
        const urlObj = new URL(url)
        const redirectUrlObj = new URL(urlObj.origin)
        redirectUrlObj.pathname = '/redirect/' + encodeURIComponent(encodeURIComponent(appUrl))
        const redirectUrl = redirectUrlObj.toString()
        console.log('MSAPPROXY: redirecting to', redirectUrl)

        // Do the actual redirect
        // console.log('MSAPPROXY: redirect CURRENTLY DISABLED!', redirectUrl)
        window.location.href = redirectUrl
      }

      // Create new success function where we remove the part of the URL that
      // prevents us from redirecting since we don't need it anymore since we
      // successfully managed to authenticate
      options.success = (...args) => {
        const noRedirect = Model.getState('noredirect')
        if (noRedirect) {
          const successMsg = 'MS App Proxy successful login using PoziServer\'s redirect functionality'
          PoziUtils.gtag({
            category: 'program',
            status: 'success',
            identifier: 'MSAPPROXY',
            action: 'ajax request',
            additionalInfo: successMsg
          })

          // Remove the fragment from the URL by removing it from the model
          console.log('MSAPPROXY: Removing \'noredirect\' from URL', url)
          Model.unset('app.state.noredirect')
          HistoryUpdateURL()
        }

        // Call original success function
        if (typeof onSuccess === 'function') {
          console.log('MSAPPROXY: Calling original success function')
          onSuccess.apply(onSuccess, args)
        }
      }
    }

    const request = $.ajax(options)

    // console.debug('jquery ajax request', options.url, request, options)

    return request
  }

  /**
   * ajaxAsPromise
   *
   * Convenience Promise wrapper for ajax calls
   *
   * @param {Object} options
   *
   * @returns {Promise}
   */
  const ajaxAsPromise = (options) => {
    return new Promise((resolve, reject) => {
      // A user can override the default error and success options
      const ajaxOptions = {
        error: reject,
        success: resolve,
        ...options
      }

      return PoziAPIData().ajax(ajaxOptions).catch(
        function (error) {
          console.log('ajaxAsPromise - unexpected error', error)
        }
      )
    })
  }

  /**
   * Can be used as a basic error handler for jQuery.ajax requests.
   * Consider avoiding this function, code should provide a detailed error handler function for the specific task.
   * @param {jqxhr} jqxhr jQuery XHR object
   * @param {string} textStatus Status of error
   * @param {Object} error Error details
   */
  const ajaxFailDefault = (jqxhr, textStatus, error, options) => {
    const errorMsg = [textStatus, error].join(' ').trim()
    const url = options?.url || 'MISSING_URL'

    console.error('ajaxFailDefault', errorMsg, url)
  }

  // Return adapters (must be at end of adapter)
  return adapters
})

export default PoziAPIData
// End A (Adapter)
